import { useEffect, useState } from "react";
import { api, money } from "@/lib/api";
import { Plus, Trash2, Edit2, X } from "lucide-react";
import { toast } from "sonner";
import ImageUploadInput from "@/components/ImageUploadInput";

const EMPTY = {
    name: "", slug: "", description: "", short_description: "",
    category: "detergents", subcategory: "",
    price: 0, currency: "USD", stock: 0,
    image_url: "", is_featured: false, is_active: true, is_promo: false, rating: 4.7, gallery: [],
};

export default function AdminProducts() {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);

    function load() {
        api.get("/products").then((r) => setItems(r.data));
    }
    useEffect(load, []);

    async function save(payload) {
        try {
            if (payload.id) {
                await api.put(`/products/${payload.id}`, payload);
                toast.success("Product updated");
            } else {
                await api.post("/products", payload);
                toast.success("Product created");
            }
            setEditing(null);
            load();
        } catch (err) {
            toast.error("Save failed: " + (err.response?.data?.detail || err.message));
        }
    }

    async function remove(id) {
        if (!confirm("Delete this product?")) return;
        await api.delete(`/products/${id}`);
        toast.success("Deleted");
        load();
    }

    return (
        <div className="p-8" data-testid="admin-products">
            <div className="flex items-center justify-between mb-8">
                <div>
                    <h1 className="font-display text-3xl font-bold">Products</h1>
                    <p className="text-sm text-muted-foreground">{items.length} item{items.length !== 1 ? 's' : ''}</p>
                </div>
                <button onClick={() => setEditing({ ...EMPTY })} className="inline-flex items-center gap-2 bg-primary text-white px-4 py-2 font-semibold hover:bg-primary-hover" data-testid="admin-create-product">
                    <Plus className="w-4 h-4" /> New product
                </button>
            </div>

            <div className="bg-white border border-border overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="bg-muted text-left">
                        <tr>
                            <th className="px-4 py-3">Product</th>
                            <th className="px-4 py-3">Category</th>
                            <th className="px-4 py-3">Price</th>
                            <th className="px-4 py-3">Stock</th>
                            <th className="px-4 py-3"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((p) => (
                            <tr key={p.id} className="border-t border-border" data-testid={`admin-product-row-${p.slug}`}>
                                <td className="px-4 py-3">
                                    <div className="flex items-center gap-3">
                                        <img src={p.image_url} alt={p.name} className="w-10 h-10 object-cover bg-surface" />
                                        <div>
                                            <div className="font-semibold">{p.name}</div>
                                            <div className="text-xs text-muted-foreground font-mono">{p.slug}</div>
                                        </div>
                                    </div>
                                </td>
                                <td className="px-4 py-3 capitalize">{p.category}</td>
                                <td className="px-4 py-3 font-semibold">{money(p.price)}</td>
                                <td className="px-4 py-3">{p.stock}</td>
                                <td className="px-4 py-3 text-right">
                                    <button onClick={() => setEditing(p)} className="p-2 hover:text-primary" data-testid={`edit-product-${p.slug}`}><Edit2 className="w-4 h-4" /></button>
                                    <button onClick={() => remove(p.id)} className="p-2 hover:text-primary" data-testid={`delete-product-${p.slug}`}><Trash2 className="w-4 h-4" /></button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {editing && <ProductForm initial={editing} onClose={() => setEditing(null)} onSave={save} />}
        </div>
    );
}

function ProductForm({ initial, onClose, onSave }) {
    const [form, setForm] = useState(initial);
    function set(k, v) { setForm((f) => ({ ...f, [k]: v })); }

    function submit(e) {
        e.preventDefault();
        const payload = {
            ...form,
            price: Number(form.price),
            stock: Number(form.stock),
            rating: Number(form.rating),
        };
        onSave(payload);
    }

    return (
        <div className="fixed inset-0 bg-ink/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
            <form onSubmit={submit} className="bg-white max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="admin-product-form">
                <div className="flex items-center justify-between mb-6">
                    <h3 className="font-display text-2xl font-bold">{form.id ? "Edit" : "New"} product</h3>
                    <button type="button" onClick={onClose} className="p-1"><X /></button>
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                    <input required value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Name" className="border border-border px-3 py-2 sm:col-span-2" />
                    <input required value={form.slug} onChange={(e) => set("slug", e.target.value)} placeholder="slug-url-friendly" className="border border-border px-3 py-2" />
                    <select value={form.category} onChange={(e) => set("category", e.target.value)} className="border border-border px-3 py-2">
                        <option value="detergents">Detergents</option>
                        <option value="ict">ICT</option>
                        <option value="software">Software</option>
                    </select>
                    <input value={form.subcategory} onChange={(e) => set("subcategory", e.target.value)} placeholder="Subcategory" className="border border-border px-3 py-2" />
                    <input type="number" step="0.01" required value={form.price} onChange={(e) => set("price", e.target.value)} placeholder="Price" className="border border-border px-3 py-2" />
                    <input type="number" value={form.stock} onChange={(e) => set("stock", e.target.value)} placeholder="Stock" className="border border-border px-3 py-2" />
                    <input value={form.image_url} onChange={(e) => set("image_url", e.target.value)} placeholder="Image URL" className="border border-border px-3 py-2 sm:col-span-2 hidden" />
                    <div className="sm:col-span-2">
                        <ImageUploadInput value={form.image_url} onChange={(v) => set("image_url", v)} purpose="product" label="Product image" />
                    </div>
                    <input value={form.short_description} onChange={(e) => set("short_description", e.target.value)} placeholder="Short description" className="border border-border px-3 py-2 sm:col-span-2" />
                    <textarea required value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="Full description" rows={4} className="border border-border px-3 py-2 sm:col-span-2" />
                    <label className="flex items-center gap-2"><input type="checkbox" checked={form.is_featured} onChange={(e) => set("is_featured", e.target.checked)} /> Featured</label>
                    <label className="flex items-center gap-2"><input type="checkbox" checked={form.is_promo} onChange={(e) => set("is_promo", e.target.checked)} /> Promo</label>
                    <label className="flex items-center gap-2"><input type="checkbox" checked={form.is_active} onChange={(e) => set("is_active", e.target.checked)} /> Active</label>
                </div>
                <div className="flex gap-3 mt-6">
                    <button type="button" onClick={onClose} className="flex-1 border border-border py-2.5 font-semibold">Cancel</button>
                    <button type="submit" className="flex-1 bg-primary text-white py-2.5 font-semibold hover:bg-primary-hover" data-testid="admin-product-save">Save</button>
                </div>
            </form>
        </div>
    );
}
